import { useState } from 'react'
import { getDeviceCategory } from '@shared/unifi-fingerprints'

interface Props {
  /** R2-cached icon URL (e.g. /api/assets/device-icons/USL24P.png) */
  iconUrl?: string | null
  /** Device type for generic fallback */
  deviceType?: string | null
  /** Model string for category inference */
  model?: string | null
  /** Render size in px */
  size?: number
  /** Additional inline styles */
  style?: React.CSSProperties
}

/**
 * Universal device icon component.
 * Resolution order: R2 cached image → generic SVG by category → unknown icon
 */
export function DeviceIcon({ iconUrl, deviceType, model, size = 32, style }: Props) {
  const [imgError, setImgError] = useState(false)

  const category = getDeviceCategory(deviceType || null, model || null)

  // If we have a cached icon URL and it hasn't errored, show the product photo
  if (iconUrl && !imgError) {
    return (
      <div style={{
        width: size, height: size, borderRadius: size > 40 ? 8 : 4,
        overflow: 'hidden', flexShrink: 0,
        background: 'var(--bg-surface)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...style,
      }}>
        <img
          src={iconUrl}
          alt=""
          loading="lazy"
          width={size}
          height={size}
          style={{ objectFit: 'contain', width: '100%', height: '100%' }}
          onError={() => setImgError(true)}
        />
      </div>
    )
  }

  // Generic SVG fallback
  const iconSvg = CATEGORY_ICONS[category] || CATEGORY_ICONS.unknown
  const color = CATEGORY_COLORS[category] || CATEGORY_COLORS.unknown

  return (
    <div style={{
      width: size, height: size, borderRadius: size > 40 ? 8 : 4,
      overflow: 'hidden', flexShrink: 0,
      background: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      ...style,
    }}>
      <svg
        width={size * 0.6}
        height={size * 0.6}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {iconSvg}
      </svg>
    </div>
  )
}

/** For use in SVG contexts (topology view) — renders just the icon paths */
export function DeviceIconSvg({ deviceType, model, x, y, size = 48, iconUrl }: {
  deviceType?: string | null
  model?: string | null
  x: number
  y: number
  size?: number
  iconUrl?: string | null
}) {
  const category = getDeviceCategory(deviceType || null, model || null)
  const color = CATEGORY_COLORS[category] || CATEGORY_COLORS.unknown

  // For product photos in SVG, use <image> element
  if (iconUrl) {
    return (
      <g>
        <rect
          x={x - size / 2} y={y - size / 2}
          width={size} height={size}
          rx={6} fill="var(--bg-surface)"
          stroke={`${color}40`} strokeWidth={1}
        />
        <image
          href={iconUrl}
          x={x - size / 2 + 2} y={y - size / 2 + 2}
          width={size - 4} height={size - 4}
          preserveAspectRatio="xMidYMid meet"
        />
      </g>
    )
  }

  // Generic icon in SVG
  const iconPaths = CATEGORY_ICONS[category] || CATEGORY_ICONS.unknown
  const iconSize = size * 0.5
  return (
    <g>
      <rect
        x={x - size / 2} y={y - size / 2}
        width={size} height={size}
        rx={6} fill={`${color}12`}
        stroke={`${color}30`} strokeWidth={1}
      />
      <svg
        x={x - iconSize / 2} y={y - iconSize / 2}
        width={iconSize} height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {iconPaths}
      </svg>
    </g>
  )
}

const CATEGORY_COLORS: Record<string, string> = {
  camera: 'var(--color-camera)',
  switch: 'var(--color-access)',
  ap: '#7c3aed',
  gateway: 'var(--color-networking)',
  nvr: '#1d4ed8',
  server: '#475569',
  access_panel: 'var(--color-power)',
  intercom: 'var(--color-cable)',
  fire_panel: 'var(--err)',
  workstation: 'var(--info)',
  laptop: 'var(--info)',
  phone: '#06b6d4',
  tablet: '#06b6d4',
  printer: 'var(--color-infra)',
  speaker: 'var(--color-cable)',
  iot: '#14b8a6',
  ups: '#ea580c',
  unknown: 'var(--color-infra)',
}

// SVG path elements for each device category (24x24 viewBox, monoline style)
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  camera: <>
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </>,
  switch: <>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="7" cy="12" r="1" fill="currentColor" />
    <circle cx="11" cy="12" r="1" fill="currentColor" />
    <circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="19" cy="12" r="1" fill="currentColor" />
  </>,
  ap: <>
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <circle cx="12" cy="20" r="1" fill="currentColor" />
  </>,
  gateway: <>
    <rect x="2" y="3" width="20" height="18" rx="2" />
    <path d="M7 8h2M7 12h2M7 16h2" />
    <circle cx="17" cy="8" r="1" fill="currentColor" />
    <circle cx="17" cy="12" r="1" fill="currentColor" />
  </>,
  nvr: <>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M6 8h.01M6 12h.01M6 16h.01" />
    <path d="M10 8h8M10 12h8M10 16h8" />
  </>,
  server: <>
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <circle cx="6" cy="6" r="1" fill="currentColor" />
    <circle cx="6" cy="18" r="1" fill="currentColor" />
  </>,
  access_panel: <>
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="12" cy="10" r="3" />
    <path d="M12 13v4" />
    <circle cx="12" cy="18" r="1" fill="currentColor" />
  </>,
  intercom: <>
    <rect x="5" y="2" width="14" height="20" rx="3" />
    <circle cx="12" cy="9" r="3" />
    <path d="M9 16h6" />
  </>,
  fire_panel: <>
    <path d="M12 2C8 6 4 10 4 14a8 8 0 0 0 16 0c0-4-4-8-8-12z" />
    <path d="M12 22c-2 0-4-2-4-4 0-2 2-4 4-6 2 2 4 4 4 6 0 2-2 4-4 4z" />
  </>,
  workstation: <>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </>,
  laptop: <>
    <path d="M20 16V6a2 2 0 00-2-2H6a2 2 0 00-2 2v10" />
    <path d="M2 16h20a1 1 0 011 1v1a2 2 0 01-2 2H3a2 2 0 01-2-2v-1a1 1 0 011-1z" />
  </>,
  phone: <>
    <rect x="5" y="2" width="14" height="20" rx="3" />
    <path d="M12 18h.01" />
  </>,
  tablet: <>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <circle cx="12" cy="18" r="1" fill="currentColor" />
  </>,
  printer: <>
    <path d="M6 9V2h12v7" />
    <rect x="4" y="9" width="16" height="8" rx="1" />
    <path d="M6 18h12v4H6z" />
  </>,
  speaker: <>
    <rect x="4" y="2" width="16" height="20" rx="4" />
    <circle cx="12" cy="14" r="4" />
    <circle cx="12" cy="6" r="2" />
  </>,
  iot: <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 1v4M12 19v4M4.2 4.2l2.8 2.8M17 17l2.8 2.8M1 12h4M19 12h4M4.2 19.8l2.8-2.8M17 7l2.8-2.8" />
  </>,
  ups: <>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M13 6l-4 6h6l-4 6" />
  </>,
  unknown: <>
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <circle cx="12" cy="17" r="0.5" fill="currentColor" />
  </>,
}

export { CATEGORY_COLORS }
